/**
 * ********************************************************************************************************
 *
 *
 * V1.2
 *
 * User: shlomif
 * Date: 22/12/2013
 *
 * - highlightText method added.
 * - class 'checkbox-label' is now used in css on span element
 *
 * ********************************************************************************************************
 *
 * V1.1
 *
 * User: margalitb
 * Date: 31/07/13
 * Visual PDF: https://docs.google.com/a/liveperson.com/file/d/0B7O_LAPhjkbkLUZpZnRnUU9hWms/edit
 *
 * This widget is used to implement a checkbox component on the page.
 *
 * Options:
 * el - mandatory. jQuery element. The element on the page where the component will be created.
 * label - optional. text for the label. The label presents only when permutation is Checkbox.PERMUTATION.A.
 * mode - optional. If Checkbox.MODE.BOOLEAN a boolean true|false for the cb value will be used(also the default behaviour). If Checkbox.MODE.TRISTATE the enum value would be used, a tri-state checkbox.
 * checked - optional. If CheckboxModel.STATES.CHECKED, the checkbox will be checked, otherwise - will be not checked.
 * enabled  - optional. Defined the checkbox state - enabled or disable. (Enabled by default). Optional values:  true / false.
 * tabindex  -  optional. Attribute tabindex for the checkbox element.
 * permutation - optional. Change the label design with font by the permutation classes. existing permutations:    Checkbox.PERMUTATION.A, Checkbox.PERMUTATION.B.
 * stopPropagationOnClick - optional default to false, currently event of click is propagated, but in case the checkbox is inside a button
 * For example the studio tabs - we dont want the click to propagate to the button
 * Using Example:
 *
 * template:
 * <div class="checkbox-wrapper"><button class="checkbox-btn"></button><span>{{label}}</span></div>
 *
 *  js:
 *
 *  var campaignCheckbox = new Checkbox({
 *      checked: CheckBoxModel.STATES.CHECKED,
 *      label: 'checkbox text',
 *      el: parentElement
 *  });
 *  campaignCheckbox.render();
 *
 *
 * *******************************************************************************************************
 *
 */
define(function (require) {
    "use strict";

    var _ = require("underscore");
    var Marionette = require("marionette");
    var DomUtilities = require('infra-util/DomUtilities');
    var template = require("ui/checkbox/templates/checkbox.tmpl");
    var CheckboxModel = require("ui/checkbox/js/models/CheckboxModel");
    var Bubble = require("ui/bubble/Bubble");


    var CLASSES = {};
    CLASSES[CheckboxModel.STATES.CHECKED] = "checkbox-btn-checked";
    CLASSES[CheckboxModel.STATES.UNCHECKED] = "checkbox-btn-unchecked";
    CLASSES[CheckboxModel.STATES.MIDCHECKED] = "checkbox-btn-mid-checked";

    var Checkbox = Marionette.ItemView.extend({
        template : template,
        withUIComponents: true,
        errorMsg:null,
        className : "checkbox-item", // Used only by composite view (checkbox list)
        tagName : "li",              // Used only by composite view (checkbox list)

        ui : {
            checkboxWrapper : ".checkbox-wrapper",
            checkbox : ".checkbox-btn",
            checkboxLabel : ".checkbox-label"
        },

        events : {
            "click .checkbox-wrapper" : "checkboxClick",
            "focus button.checkbox-btn":"openBubble",
            "blur button.checkbox-btn":"onCheckboxBlur",
            "mouseenter button.checkbox-btn":"openBubble",
            "mouseleave button.checkbox-btn":"closeBubble"
        },

        modelEvents : {
            "change:enabled" : "renderEnabled",
            "change:checked" : "renderChecked"
        },

        templateHelpers : function () {
            return {
                checkedClass : this._getCheckedClass(),
                permutationClass : this.permutation,
                tabindex : this.tabindex
            };
        },

        initialize : function (options) {
            var mode= options.mode || CheckboxModel.MODES.BOOLEAN;
            var checked = options.checked;
            if(_.isBoolean(checked)) {
                checked= checked ? CheckboxModel.STATES.CHECKED : CheckboxModel.STATES.UNCHECKED;
            }

            if (options.el) {
                options.label = this.$el.data('label') || options.label;
                options.enabled = this.$el.data('enabled') || options.enabled;
            }

            this.model = new CheckboxModel({
                key: options.key,
                mode: mode,
                label : options.label,
                enabled : options.enabled === undefined ? true : options.enabled
            });
            this.setCheckedState(checked);

            this.permutation = options.permutation || Checkbox.PERMUTATION.A;
            this.tabindex = options.tabindex || "";
            this.errorPosition = options.errorPosition || Checkbox.ERROR_POSITION.BOTTOM;
            this.stopPropagationOnClick = options.stopPropagationOnClick || false;
            this.tooltip =  _.has(options, "tooltip") ? options.tooltip : "";
        },

        /**
         * Add some attributes after the checkbox HTML element rendered.
         */
        onRender : function () {
            // If required, initialize the checkbox element disabled.
            this.renderEnabled(this.model, this.model.get("enabled"));
            this.setTooltip(this.tooltip);
        },

        /** Upon cb click
         * if cb is unchecked then set it as checked
         * if cb is either checked or mid-checked set it unchecked
         * @param ev
         */
        checkboxClick : function (ev) {
            if (this.isEnabled()) {
                this.setError(false);
                // Toggle check state. Note only from unchecked will we toggle to checked
                var setChecked = this.isUnChecked();
                this.setCheckedState(setChecked);
                //trigger the change to the user with the new value
                this.trigger(Checkbox.TRIGGERING.CHANGE, this.getChecked());
            }
            if (this.stopPropagationOnClick) {
                ev.stopPropagation();
            }
        },

        getCheckedState : function () {
            return this.model.get("checked");
        },

        isChecked : function () {
            return this.model.get("checked") === CheckboxModel.STATES.CHECKED;
        },

        isUnChecked : function () {
            return this.model.get("checked") === CheckboxModel.STATES.UNCHECKED;
        },

        /**
         * depending on the mode a boolean or an ENUM would be returned as the component's value
         * @returns {*}
         */
        getChecked : function () {
           return this.model.get('mode') === CheckboxModel.MODES.BOOLEAN ? this.isChecked() : this.getCheckedState();
        },

        /**
         * A parameter overloaded functions that can accept either boolean or ENUM and finally applies
         * the model with one of the 3 STATES enum values.
         * @param checked {boolean|STATES ENUM} - can be both a boolean or one of the STATES ENUM values
         */
        setCheckedState : function (checked) {
            //default
            if(checked === undefined){
               checked = CheckboxModel.STATES.UNCHECKED;
            }
            //boolean mode, mid checked not allowed
            if(this.model.get('mode') === CheckboxModel.MODES.BOOLEAN && checked === CheckboxModel.STATES.MIDCHECKED){
                checked = CheckboxModel.STATES.UNCHECKED;
            }
            //transform boolean value to internal enum, hence overloading this function
            if(_.isBoolean(checked)) {
                checked = checked ? CheckboxModel.STATES.CHECKED : CheckboxModel.STATES.UNCHECKED;
            }
            this.model.set("checked", checked);
        },

        /**
         *
         * @param model
         * @param checked
         */
        renderChecked : function (model, checked) {
            _.each(CLASSES, _.bind(function(value){
                this.ui.checkbox.removeClass(value);
            }, this));
            this.ui.checkbox.addClass(this._getCheckedClass());
        },

        _getCheckedClass: function(){
            return CLASSES[this.model.get("checked")];
        },

        /**
         * set the state of the checkbox to enabled/disabled
         * @param {true / false} For checkbox enabled - true, otherwise - false.
         */
        setEnabled : function (enabled) {
            this.model.set("enabled", enabled);
        },

        renderEnabled : function (model, enabled) {
            this.ui.checkboxWrapper.toggleClass("disabled", !enabled);
            this.ui.checkbox.prop("disabled", !enabled);
        },
        /**
         * @return {true / false} -  For checkbox enabled - true, otherwise - false.
         */
        isEnabled : function () {
            return this.model.get("enabled");
        },


        setError:function (isError, msg) {
            if (isError === true) {
                this.errorMsg = msg;
                this.ui.checkbox.addClass("error");
            }
            else {
                this.ui.checkbox.removeClass("error");
                this.errorMsg = null;
                this.closeBubble();
            }
        },

        closeBubble: function(event, force){
            if (this.uiComponents.CheckboxError) {
                this.uiComponents.CheckboxError.close(force);
            }
        },

        /**
         * Triggered when the checkbox is blur
         */
        onCheckboxBlur:function () {
            if(this.ui.checkbox.hasClass("error")) {
                this.trigger('blurError');
            }
            this.setError(false);
        },

        /**
         * Triggered when the checkbox is on focus
         */
        openBubble:function () {
            if (this.ui.checkbox.hasClass("error") && this.errorMsg) {
                this.closeBubble(null, true);
                this.uiComponents.host$.createAndRender("CheckboxError", {
                    type:Bubble,
                    options:{
                        targetControl:this.ui.checkbox,
                        text:this.errorMsg,
                        position:this.errorPosition,
                        icon:Bubble.ICON.ERROR,
                        showCloseButton:false
                    }
                });
            }
        },

        setCheckboxLabel: function (label) {
            this.ui.checkboxLabel.text(label);
        },

        setTooltip: function(tooltip) {
            if(!_.isEmpty(tooltip)){
                this.tooltip = tooltip;
                this.ui.checkboxWrapper.attr("title", this.tooltip);
            }
        },

        /**
         * Wraps occurrences of the text with a span with the specified class name
         * @param textToHighlight
         * @param className
         */
        highlightText: function(textToHighlight, className) {
            var re = new RegExp(textToHighlight, 'i');
            var element = this.ui.checkboxLabel;
            var highlighted = element.clone();
            DomUtilities.highlight.call(this, highlighted[0], re, 'span', className);
            element.html(highlighted.html());   // Must replace the html as a whole, otherwise some weird width
            // issues occur (the width of the container div of the checkbox
            // doesn't get updated).
        }
    });

    Checkbox.ERROR_POSITION = Bubble.POSITION;
    Checkbox.TRIGGERING = {
        CHANGE : "change"
    };



    Checkbox.STATES= CheckboxModel.STATES;
    Checkbox.MODES= CheckboxModel.MODES;


    /**
     * Public static eNum of the optional permutation.
     * perm-A: checkbox with label.
     * perm-B: checkbox without label.
     */
    Checkbox.PERMUTATION = {
        A : "perm-A",
        B : "perm-B",
        C : "perm-C",
        BIG: "perm-big",
        NIGHT: "perm-night"
    };

    return Checkbox;
});
